import { useState } from 'react';
import { Alert, Button, Group, List, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SEPARATION_OF_DUTY_WARNINGS,
  findSystemRole,
  hasCapability,
  isSubsetOfGrants,
  type Grant,
} from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';
import { GrantMatrix } from '../components/GrantMatrix.js';
import { PageHeader } from '../components/Page.js';

interface RoleRow {
  _id: string;
  orgId: string | null;
  key: string;
  name: string;
  description: string;
  isSystem: boolean;
  level: number;
  grants: Grant[];
}

/** Предупреждения о совмещении обязанностей — предупреждаем, но не запрещаем. */
function dutyWarnings(grants: readonly Grant[]): string[] {
  const active = new Set(grants.filter((g) => g.scope !== 'none').map((g) => g.capability));
  return SEPARATION_OF_DUTY_WARNINGS.filter(
    ({ pair }) => active.has(pair[0]) && active.has(pair[1]),
  ).map(({ message }) => message);
}

export function Roles(): React.JSX.Element {
  const queryClient = useQueryClient();
  const actor = useSession((s) => s.actor);
  const canEdit = actor !== null && hasCapability(actor, 'role:write');

  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [cloneKey, setCloneKey] = useState<string | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<{ roles: RoleRow[] }>('/roles'),
  });

  const save = useMutation({
    mutationFn: async () => {
      setError(null);
      if (editing !== null) {
        return api(`/roles/${editing._id}`, { method: 'PATCH', body: { name, grants } });
      }
      return api('/roles', {
        method: 'POST',
        body: { name, description: '', clonedFromKey: cloneKey ?? '', grants },
      });
    },
    onSuccess: async () => {
      close();
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not save the role');
    },
  });

  const close = (): void => {
    setEditing(null);
    setCreating(false);
    setName('');
    setCloneKey(null);
    setGrants([]);
    setError(null);
  };

  const startClone = (key: string): void => {
    const source = findSystemRole(key);
    if (source === undefined) return;
    setCloneKey(key);
    setName(`${source.name} (copy)`);
    setGrants([...source.grants]);
    setCreating(true);
  };

  const roles = data?.roles ?? [];
  const warnings = dutyWarnings(grants);
  const withinActor = actor === null || isSubsetOfGrants(grants, actor.grants);

  return (
    <Stack gap="md">
      <PageHeader
        title="Roles"
        count={roles.length}
        hint='A role is created by cloning a system role, not from a blank slate: the real request sounds like "same as the manager, but without export". System roles are immutable.'
        action={
          canEdit ? (
            <Select
              size="xs"
              w={200}
              placeholder="Clone from…"
              data={roles.filter((r) => r.isSystem).map((r) => ({ value: r.key, label: r.name }))}
              value={null}
              onChange={(value) => {
                if (value !== null) startClone(value);
              }}
            />
          ) : undefined
        }
      />

      <Stack gap={0}>
        {roles.map((role) => (
          <div
            key={role._id}
            style={{ borderBottom: '1px solid var(--pf-line)', padding: '14px 16px' }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
              <div style={{ minWidth: 0 }}>
                <Group gap={8} align="baseline">
                  <Text fw={640} fz="sm">
                    {role.name}
                  </Text>
                  {/* Системная роль — факт, а не тяжесть: подпись, не цветной бейдж. */}
                  <Text className="pf-eyebrow">{role.isSystem ? 'system' : 'custom'}</Text>
                  <Text className="pf-num" fz="xs" c="dimmed">
                    level {role.level}
                  </Text>
                </Group>
                <Text c="dimmed" fz="sm" mt={2} maw="70ch">
                  {role.description || '—'}
                </Text>
                <Text c="dimmed" fz="xs" mt={4}>
                  {role.grants.filter((g) => g.scope !== 'none').length} permissions
                </Text>
              </div>

              <Button
                size="compact-xs"
                variant="default"
                disabled={!canEdit || role.isSystem}
                style={{ flex: '0 0 auto' }}
                onClick={() => {
                  setEditing(role);
                  setName(role.name);
                  setGrants([...role.grants]);
                }}
              >
                {role.isSystem ? 'Immutable' : 'Edit'}
              </Button>
            </Group>
          </div>
        ))}
      </Stack>

      <Modal
        opened={editing !== null || creating}
        onClose={close}
        size="xl"
        title={editing !== null ? `Role "${editing.name}"` : 'New role'}
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />

          <GrantMatrix
            grants={grants}
            actorGrants={actor?.grants ?? []}
            onChange={setGrants}
          />

          {warnings.length > 0 && (
            <Alert color="orange" variant="light" title="Separation of duties">
              <List size="sm">
                {warnings.map((w) => (
                  <List.Item key={w}>{w}</List.Item>
                ))}
              </List>
              <Text fz="xs" c="dimmed" mt={6}>
                This is a warning, not a block: in a twenty-truck fleet one person does
                everything.
              </Text>
            </Alert>
          )}

          {!withinActor && (
            <Alert color="red">
              This role has permissions broader than yours. You cannot grant more than you have.
            </Alert>
          )}

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            disabled={name.trim() === '' || !withinActor}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
